import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import fleetApi from '../../services/api.service';
import { buildUrlWithParams } from '../../utils/url.utils';

const adaptMovementsResponse = (apiResponse) => {
  const { total = 0, page = 1, limit = 20, data = [] } = apiResponse ?? {};

  return {
    pagination_info: {
      current_items_count: data.length,
      items_per_page: limit,
      total_items: total,
      current_page: page,
      total_pages: Math.max(1, Math.ceil(total / limit)),
    },
    items: data.map((movement) => ({
      ...movement,
      id: movement.movement_id,
    })),
  };
};

export default function useGetAddressConsignmentMovements(
  addressId,
  filters,
  delay = 0
) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceTimeout = useRef(null);
  const abortRef = useRef(null);

  const page = Math.floor(Number(filters.offset) / Number(filters.limit)) + 1;

  const fetchData = useCallback(async () => {
    if (!addressId) {
      setData(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);
    try {
      const url = buildUrlWithParams('/consignment/movements', {
        address_id: addressId,
        page,
        limit: filters.limit,
        packaging_id: filters.packaging_id,
      });
      const response = await fleetApi.get(url, { signal: controller.signal });

      if (!controller.signal.aborted) {
        setData(adaptMovementsResponse(response.data));
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setError(
          err.response?.data?.message ||
            err.message ||
            'Une erreur est survenue'
        );
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [addressId, page, filters.limit, filters.packaging_id]);

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      fetchData();
    }, delay);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [fetchData, delay]);

  const mutate = useMemo(() => fetchData, [fetchData]);

  return { data, isLoading, error, mutate };
}
